/**
 * The one place the engine builds a parser context (D211).
 *
 * @remarks
 * Six call sites used to assemble `{ entities, lexicon, … }` by hand, and the
 * day the context grew a field (`knownWords` — D211 the pack's declared
 * words, D212 every word its prose speaks: the fuzzy rung must never read
 * either as a typo; `claimedNames` — D227 the names it declares in more than
 * one word) that was six places to remember it and six to forget. The context is one shape with one meaning —
 * "parse this against this world, referable as of now" — so it is built
 * here, and a caller adds only what is its own: a recency view, an option.
 *
 * @packageDocumentation
 */

import type { EntityRef, ParseCandidateEntity, ParseContext, ParserOptions, PronounAntecedents } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

/** What a caller adds to the world's half of the context. */
export interface ParseContextExtras {
	readonly options?: ParserOptions;
	/** D235: the conversational partner's pronoun naming the partner, ahead of recency (`conversational-partner.ts`). */
	readonly pronounAntecedents?: PronounAntecedents;
	readonly recentEntityRefs?: readonly EntityRef[];
}

/**
 * Build the parser's context for one parse: the bundle's lexicon and
 * declared words, the catalog the caller derived, and whatever the caller
 * adds.
 *
 * @param bundle - The loaded bundle
 * @param entities - What is referable for this parse (the per-turn catalog, or a narrowed one)
 * @param extras - The caller's own additions, if any
 * @returns The context `parseCommand` takes
 */
export const buildParseContext = (
	bundle: EngineGameBundle,
	entities: readonly ParseCandidateEntity[],
	extras: ParseContextExtras = {},
): ParseContext => ({
	// D227: the names the pack claims beyond this turn's catalog — a phrase spelling one is never read as a shorter head here.
	claimedNames: bundle.indexes.claimedNames,
	entities,
	knownWords: bundle.indexes.knownWords,
	lexicon: bundle.compiledLexicon,
	...extras,
});
